<script setup lang="ts">
import itemImage from '@/components/itemImage.vue'
import {computed, ref, watch, onMounted} from "vue";
import Diamond360Defaults from "@/components/diamonds/diamond360Defaults.vue";
import { PanchImages } from '@/assets/ts/PanchImages';

const host = process.env.VUE_APP_HOST || "";

const props = defineProps<{
  certificate: string
  diamond: {
    image_url?: string;
    form: string;
    institute?: string;
    certificate?: string;
  };
  imageIdx?: string | number;
}>()

const certificateUrl = computed(() => {
  return props.certificate;
});

const toConfidence = () => {
  window.open('/diamanten/5cs#confidence', '_blank');
}

const emits = defineEmits([
  "switchImage"
]);

let isProgrammaticScroll = false;
let timeout: any = null;

const watchScroll = () => {
  if (isProgrammaticScroll || !scrollContainer.value) return;
  getImageInViewport();
}

const panchImageUrl = ref('');
const scrollContainer = ref<HTMLElement | null>(null);

const checkPanchImage = async () => {
  if (!props.diamond.image_url && props.diamond.certificate) {
    const panch = new PanchImages();
    panchImageUrl.value = (await panch.getStill(props.diamond.certificate)) as string;
  } else {
    panchImageUrl.value = '';
  }
};

onMounted(checkPanchImage);

const realImageUrl = computed(() => {
  return props.diamond.image_url || panchImageUrl.value;
});

const defaultImageUrl = computed(() => {
  return (process.env.VUE_APP_HOST || "") + "/img/diamonds/cuts/cut_" + props.diamond.form.toLowerCase() + "@2x.jpg";
});

watch(
    () => props.diamond,
    checkPanchImage,
    {deep: true},
);

watch(
    () => props.imageIdx,
    (imageIdx: string | number | undefined) => {
      if (imageIdx === undefined) return;
      isProgrammaticScroll = true;
      if (timeout) clearTimeout(timeout);

      scrollContainer.value?.querySelector("#imageIdx" + imageIdx)?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });

      // Reset the flag after the smooth scroll is likely finished
      timeout = setTimeout(() => {
        isProgrammaticScroll = false;
      }, 500);
    },
    {deep: true},
);

const getImageInViewport = () => {
  checkImgaeInViewport("imageIdx-1", "-1")
  checkImgaeInViewport("imageIdx0", "0")
  checkImgaeInViewport("imageIdx1", "1")
  checkImgaeInViewport("imageIdx2", "2")
}

const checkImgaeInViewport = (nodeId: string, imagePoxIdx: string) => {
  const el = scrollContainer.value?.querySelector("#" + nodeId) as HTMLElement;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const parent = scrollContainer.value; // The scroll container
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();

  // Check if the center of the element is within the viewport of the scroll container
  const elementCenter = rect.left + rect.width / 2;
  const parentCenter = parentRect.left + parentRect.width / 2;

  // If the element is mostly visible in the center (allowing some margin)
  if (Math.abs(elementCenter - parentCenter) < rect.width / 2) {
    emits('switchImage', imagePoxIdx)
  }
}
</script>

<template>
  <div
      ref="scrollContainer"
      class="w-full overflow-x-auto overflow-y-hidden sticky snap-x snap-mandatory lg:snap-none no-scrollbar scroll-smooth"
      @scroll.passive="watchScroll"
  >
    <ul
        class="grid grid-flow-col auto-cols-max lg:grid-cols-2 lg:grid-flow-row lg:auto-cols-auto lg:gap-2 p-0.5 bg-white relative"
    >
      <li id="imageIdx-1" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none" style="aspect-ratio: 1/1">
        <div class="aspect-square bg-gray-100 flex items-center justify-center relative group w-full h-full">
          <itemImage :image-url="realImageUrl || defaultImageUrl" :zoom="true" />
          <div
              v-if="!realImageUrl"
              class="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-lg bg-brand-main/60 px-2 py-0.5 text-[8px] uppercase tracking-widest text-white z-50 pointer-events-none"
          >
            {{ $t('components.diamonds.details.DiamondImages.example_image') }}
          </div>
        </div>
      </li>
      <li v-if="false" id="imageIdx1" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none" style="aspect-ratio: 1/1">
        <div class="p-20 flex items-center justify-center h-full">
          <img
              @click="toConfidence"
              :src="`${host}/img/diamonds/diamond_details_images_sustainable@1x.png`"
              :srcset="`${host}/img/diamonds/diamond_details_images_sustainable@2x.png`"
              alt=""
              class="w-full my-auto cursor-pointer"
          />
        </div>
      </li>
      <li id="imageIdx2" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none" style="aspect-ratio: 1/1">
        <div class="p-4 flex items-center justify-center h-full">
          <diamond360-defaults :certificate="diamond.certificate" :image_url="diamond.image_url" :diamond-form="diamond.form"/>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
